import {
    Anchor,
    Button,
    Card,
    Center,
    PasswordInput,
    PinInput,
    Stack,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api';
import { apiErrorMessage } from '../api/client';
import { t } from '../dictionary';
import { useAuth } from '../providers/auth-context';

export const ForgotPasswordPage = () => {
    const { setPartner } = useAuth();
    const [step, setStep] = useState<'email' | 'reset'>('email');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    const sendCode = async () => {
        if (!/^\S+@\S+\.\S+$/.test(email)) {
            notifications.show({ color: 'red', message: 'Enter a valid email' });

            return;
        }

        setLoading(true);

        try {
            await api.recover(email);
            notifications.show({ color: 'green', message: t.auth.codeSent });
            setStep('reset');
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setLoading(false);
        }
    };

    const reset = async () => {
        if (code.length !== 6) {
            notifications.show({ color: 'red', message: t.auth.codeInvalid });

            return;
        }

        if (password.length < 8) {
            notifications.show({ color: 'red', message: t.auth.passwordHint });

            return;
        }

        setLoading(true);

        try {
            const { data } = await api.resetPassword({ email, code, password });

            setPartner(data.partner);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card padding='xl' shadow='sm'>
            <Stack gap='md'>
                <div>
                    <Title order={2}>{t.auth.forgotTitle}</Title>
                    <Text c='dimmed' size='sm' mt={4}>
                        {t.auth.forgotSubtitle}
                    </Text>
                </div>

                {step === 'email' && (
                    <>
                        <TextInput
                            label={t.auth.email}
                            placeholder='you@example.com'
                            value={email}
                            onChange={(e) => setEmail(e.currentTarget.value)}
                        />
                        <Button fullWidth loading={loading} onClick={sendCode}>
                            {t.auth.sendCode}
                        </Button>
                    </>
                )}

                {step === 'reset' && (
                    <>
                        <Center>
                            <PinInput
                                length={6}
                                type='number'
                                size='lg'
                                value={code}
                                onChange={setCode}
                                oneTimeCode
                                autoFocus
                            />
                        </Center>
                        <PasswordInput
                            label={t.auth.newPassword}
                            description={t.auth.passwordHint}
                            value={password}
                            onChange={(e) => setPassword(e.currentTarget.value)}
                        />
                        <Button fullWidth loading={loading} onClick={reset}>
                            {t.auth.resetButton}
                        </Button>
                    </>
                )}

                <Text ta='center' size='sm'>
                    <Anchor component={Link} to='/login'>
                        {t.auth.backToLogin}
                    </Anchor>
                </Text>
            </Stack>
        </Card>
    );
};
